import { ReportPdfData } from '../types';
import { createEyebrow, createFooter } from '../utils';
import { PdfAssets } from '../../pdfTemplates';

/**
 * Creates the next steps page for the PDF report
 * @param data Report data
 * @param assets Logo and font assets for the PDF
 * @param colors Color palette for styling
 * @param pageNumber Current page number
 * @param totalPages Total number of pages
 * @returns Next steps page content for pdfmake
 */
export const createNextStepsPage = (
  data: ReportPdfData,
  assets: PdfAssets | undefined,
  colors: Record<string, string>,
  pageNumber: number,
  totalPages: number
) => {
  return [
    // Eyebrow section
    ...createEyebrow('Next Steps', assets?.markUrl, colors),
    
    // Content section
    {
      stack: [
        {
          text: 'Next Steps',
          fontSize: 22,
          bold: true,
          color: colors.black,
          margin: [64, 48, 0, 32]
        },
        
        // Intro
        {
          text: 'Based on the architecture outlined in this report, we recommend the following next steps to implement and optimize your MetaRouter infrastructure.',
          fontSize: 9,
          bold: true,
          color: colors.darkGrey,
          margin: [64, 0, 64, 24],
          alignment: 'justify'
        },
        
        // Divider
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 467,
              y2: 0,
              lineWidth: 1,
              lineColor: colors.lightGrey
            }
          ],
          margin: [64, 0, 0, 24]
        },
        
        // Two column content - Recommended Actions & Implementation Timeline
        {
          columns: [
            {
              stack: [
                {
                  text: 'Recommended Actions',
                  fontSize: 14,
                  bold: true,
                  color: colors.black,
                  margin: [0, 0, 0, 8]
                },
                {
                  ol: [
                    'Review this architecture with your technical team',
                    'Identify any additional data sources or destinations needed',
                    'Schedule a follow-up consultation with your MetaRouter representative',
                    'Begin planning your implementation timeline',
                    'Prepare your development resources for implementation'
                  ],
                  fontSize: 9,
                  color: colors.darkGrey,
                  margin: [0, 0, 0, 0]
                }
              ],
              width: '50%',
              margin: [0, 0, 12, 0]
            },
            {
              stack: [
                {
                  text: 'Implementation Timeline',
                  fontSize: 14,
                  bold: true,
                  color: colors.black,
                  margin: [0, 0, 0, 8]
                },
                {
                  ol: [
                    { text: 'Discovery & Planning (1-2 weeks)' },
                    { text: 'Initial Setup & Configuration (2-3 weeks)' },
                    { text: 'Testing & Validation (1-2 weeks)' },
                    { text: 'Production Deployment (1 week)' },
                    { text: 'Monitoring & Optimization (Ongoing)' }
                  ],
                  fontSize: 9,
                  color: colors.darkGrey,
                  margin: [0, 0, 0, 0]
                }
              ],
              width: '50%',
              margin: [12, 0, 0, 0]
            }
          ],
          margin: [64, 0, 40, 24]
        },
        
        // Divider
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 467,
              y2: 0,
              lineWidth: 1,
              lineColor: colors.lightGrey
            }
          ],
          margin: [64, 0, 0, 24]
        },
        
        // Contact Information
        {
          stack: [
            {
              text: 'Contact Information',
              fontSize: 14,
              bold: true,
              color: colors.black,
              margin: [0, 0, 0, 4],
            },
            {
              text: data.authorName || data.authorEmail,
              fontSize: 10,
              bold: true,
              color: colors.black,
              margin: [0, 0, 0, 0],
              lineHeight: 1.2,
            },
            {
              text: data.ownerRole || '',
              fontSize: 10,
              color: colors.black,
              margin: [0, 0, 0, 0],
              lineHeight: 1.2,
            },
            ...(typeof data.authorName === 'string' && data.authorName.trim() !== '' ? [{
              text: data.authorEmail,
              fontSize: 10,
              color: colors.black,
            }] : [])
          ],
          margin: [64, 0, 40, 0]
        }
      ]
    },
    
    // Footer
    createFooter(pageNumber, totalPages, colors)
  ];
};
